"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Check, RotateCcw, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { cdnImageUrlFromPath } from "@/lib/design-search/cdn-image";
import { DesignSearchResultImage } from "@/components/design-search-result-image";
import { formatLocarnoCode } from "@/lib/patent-codes";

export interface SearchResult {
  id: string;
  score: number;
  metadata: {
    patent_id?: string;
    path?: string;
    title?: string;
    assignee?: string;
    kind?: string;
    legal_status?: string;
    locarno_class_code?: string;
    locarno_subclass_code?: string;
    priority_date?: number;
    publication_date?: number;
    publication_type?: string;
    [key: string]: unknown;
  };
}

export function formatDate(dateNum: number | undefined): string | null {
  if (!dateNum) return null;
  const str = String(dateNum);
  if (str.length !== 8) return null;
  const year = str.slice(0, 4);
  const month = str.slice(4, 6);
  const day = str.slice(6, 8);
  return `${year}-${month}-${day}`;
}

export function ScoreSparkline({
  allScores,
  currentIndex,
  scorePercent,
}: {
  allScores: number[];
  currentIndex: number;
  scorePercent: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const min = Math.min(...allScores);
  const max = Math.max(...allScores);
  const range = max - min || 1;
  const minPct = Math.round(min * 100);
  const maxPct = Math.round(max * 100);

  const H = 16;
  const pad = 5;
  const cy = H / 2;

  const toX = (score: number) => ((score - min) / range) * (width - pad * 2) + pad;
  const currentX = toX(allScores[currentIndex]);

  return (
    <div className="flex items-center gap-0.5" role="img" aria-label={`Match score ${scorePercent}%, range ${minPct}% to ${maxPct}%`}>
      {minPct > 0 && <ChevronsLeft className="h-2.5 w-2.5 shrink-0 text-muted-foreground/40" aria-hidden="true" />}
      <span className="shrink-0 text-[9px] tabular-nums text-muted-foreground/60" aria-hidden="true">{minPct}%</span>
      <div ref={containerRef} className="relative min-w-0 flex-1">
        {width > 0 && (
          <>
            <svg width={width} height={H} className="block" aria-hidden="true">
              <line
                x1={pad} y1={cy} x2={width - pad} y2={cy}
                stroke="hsl(var(--muted-foreground) / 0.15)"
                strokeWidth={1}
              />
              {allScores.map((score, i) => i !== currentIndex && (
                <circle
                  key={i}
                  cx={toX(score)} cy={cy} r={2}
                  fill="hsl(var(--muted-foreground) / 0.3)"
                />
              ))}
              <circle cx={currentX} cy={cy} r={4} fill="hsl(var(--primary))" />
            </svg>
            <span
              className="absolute -top-2.5 text-[9px] font-semibold tabular-nums text-primary"
              style={{ left: currentX, transform: 'translateX(-50%)' }}
            >
              {scorePercent}%
            </span>
          </>
        )}
      </div>
      <span className="shrink-0 text-[9px] tabular-nums text-muted-foreground/60" aria-hidden="true">{maxPct}%</span>
      {maxPct < 100 && <ChevronsRight className="h-2.5 w-2.5 shrink-0 text-muted-foreground/40" aria-hidden="true" />}
    </div>
  );
}

export function ResultCard({
  result,
  allScores,
  currentIndex,
  isSelected,
  onSelect,
  onConfirm,
  onRetake,
}: {
  result: SearchResult;
  allScores: number[];
  currentIndex: number;
  isSelected?: boolean;
  onSelect?: (r: SearchResult) => void;
  onConfirm?: () => void;
  onRetake?: () => void;
}) {
  const patentId = (result.metadata.patent_id as string) || result.id;
  const imagePath = (result.metadata.path as string) || result.id;
  const imageUrl = cdnImageUrlFromPath(imagePath);
  const locarno = formatLocarnoCode(
    result.metadata.locarno_class_code,
    result.metadata.locarno_subclass_code
  );
  const priorityDate = formatDate(result.metadata.priority_date);
  const publicationDate = formatDate(result.metadata.publication_date);
  const scorePercent = Math.round(result.score * 100);

  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-label={onSelect ? `Select patent ${patentId}` : undefined}
      aria-pressed={onSelect ? isSelected : undefined}
      onClick={onSelect ? (e) => {
        const card = e.currentTarget as HTMLElement;
        onSelect(result);
        setTimeout(() => {
          card.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      } : undefined}
      onKeyDown={onSelect ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(result);
          setTimeout(() => {
            (e.target as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }
      } : undefined}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-2xl border bg-card shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl",
        onSelect && "cursor-pointer active:scale-[0.98]",
        isSelected && "border-primary ring-2 ring-primary ring-offset-2"
      )}
    >
      <div className="space-y-1 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          {result.metadata.title ? (
            <p className="line-clamp-1 min-w-0 text-xs font-bold text-foreground">{result.metadata.title}</p>
          ) : (
            <span />
          )}
          {result.metadata.legal_status && (
            <span
              className={cn(
                "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium",
                result.metadata.legal_status.toLowerCase() === "active"
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-300"
              )}
            >
              {result.metadata.legal_status}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          {result.metadata.assignee ? (
            <span className="line-clamp-1 min-w-0 text-xs text-muted-foreground">{result.metadata.assignee}</span>
          ) : (
            <span />
          )}
          <span className="line-clamp-1 shrink-0 text-xs font-medium tracking-wide tabular-nums text-foreground/70">{patentId}</span>
        </div>
      </div>

      <div className="relative overflow-hidden border-t border-border/40 bg-neutral-gray">
        <DesignSearchResultImage src={imageUrl} alt={`Patent ${patentId}`} />
      </div>

      <div className="space-y-0.5 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          {locarno && (
            <span className="line-clamp-1 min-w-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              LOC {locarno.code}
              {locarno.description && (
                <span className="ml-1 font-normal opacity-70">{locarno.description}</span>
              )}
            </span>
          )}
          {(priorityDate || publicationDate) && (
            <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/70">
              {priorityDate && publicationDate
                ? `${priorityDate} → ${publicationDate}`
                : publicationDate
                  ? publicationDate
                  : priorityDate}
            </span>
          )}
        </div>
      </div>
      <div className="border-t border-border/40 px-3 pb-2 pt-4">
        <ScoreSparkline allScores={allScores} currentIndex={currentIndex} scorePercent={scorePercent} />
      </div>

      {isSelected && onConfirm && onRetake && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-xl bg-black/60 p-4">
          <Button
            size="lg"
            className="h-12 w-full max-w-48 rounded-xl text-base font-semibold"
            onClick={(e) => {
              e.stopPropagation();
              onConfirm();
            }}
          >
            <Check className="mr-2 size-5 shrink-0" strokeWidth={1.5} />
            Accept
          </Button>
          <Button
            variant="secondary"
            size="lg"
            className="h-12 w-full max-w-48 rounded-xl text-base"
            onClick={(e) => {
              e.stopPropagation();
              onRetake();
            }}
          >
            <RotateCcw className="mr-2 size-5 shrink-0" strokeWidth={1.5} />
            Retake
          </Button>
        </div>
      )}
    </div>
  );
}
